import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { isAdmin, describeRoles } from '../../types/auth';
import {
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  AcademicCapIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { messagesAPI, registrationsAPI, boardAPI } from '../../services/api';
import { signalRService } from '../../services/signalr';
import { useClubSettings } from '../../contexts/ClubSettingsContext';
import SearchModal from './SearchModal';
import { useGlobalModal } from '../../contexts/GlobalModalContext';
import EventForm from '../events/EventForm';
import PlayerModal from '../players/PlayerModal';
import BottomNav from './BottomNav';
import MobileMenuSheet from './MobileMenuSheet';
import { topNavItem, getNavigationGroups } from './navigation';
import { REGISTRATIONS_CHANGED_EVENT } from '../../services/registrationEvents';
import { clubLogoSrc, handleClubLogoError } from '../../utils/clubLogo';
import BrandLockup from './BrandLockup';
import ModuleGuide from '../help/ModuleGuide';
import { useLearningMode } from '../../contexts/LearningModeContext';
import { useWhatsNew } from '../../hooks/useWhatsNew';
import { useAiStatus } from '../../hooks/useAiStatus';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const { settings: clubSettings } = useClubSettings();
  const { enabled: learningMode, toggle: toggleLearningMode } = useLearningMode();
  const { hasUnseen: hasNews } = useWhatsNew();
  const { modals, closeEventForm, closePlayerForm, onEventSaved, onPlayerSaved } = useGlobalModal();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingRegistrations, setPendingRegistrations] = useState(0);
  const [boardUnread, setBoardUnread] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const stored = localStorage.getItem('navExpandedGroups');
    if (stored) {
      try { return JSON.parse(stored); } catch { /* ignore */ }
    }
    return { 'Sport': true, 'Klub kezelése': true, 'Pályák': false, 'Rendszer': false };
  });

  const toggleGroup = (title: string) => {
    setExpandedGroups(prev => {
      const next = { ...prev, [title]: !prev[title] };
      localStorage.setItem('navExpandedGroups', JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    if (!user) return;

    const loadUnreadCount = async () => {
      try {
        const count = await messagesAPI.getUnreadCount();
        setUnreadCount(count);
      } catch (error) {
        console.error('Failed to load unread count:', error);
      }
    };

    loadUnreadCount();

    // Listen for new messages to update unread count
    const unsubscribe = signalRService.onMessage(() => {
      loadUnreadCount();
    });

    return () => unsubscribe();
  }, [user]);

  /**
   * Olvasatlan üzenőfal bejegyzések. Az üzenőfal maga olvasottnak jelöli, amit
   * megmutatott, és kilő egy "board-read" eseményt - erre frissítjük a számot,
   * hogy a badge ne maradjon ott a fal megnyitása után sem.
   */
  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const loadBoardUnread = async () => {
      try {
        const count = await boardAPI.getUnreadCount();
        if (!cancelled) setBoardUnread(count);
      } catch (error) {
        console.error('Az üzenőfal olvasatlan száma nem tölthető be:', error);
      }
    };

    loadBoardUnread();
    window.addEventListener('board-read', loadBoardUnread);

    return () => {
      cancelled = true;
      window.removeEventListener('board-read', loadBoardUnread);
    };
  }, [user]);

  /**
   * Függőben lévő regisztrációk figyelése. A push értesítés csak akkor ér el az
   * adminhoz, ha engedélyezte és be van iratkozva, ezért a nyitott appban is
   * jelezzük - a menüben számmal, új jelentkezésnél felugró üzenettel.
   */
  useEffect(() => {
    if (!isAdmin(user)) {
      setPendingRegistrations(0);
      return;
    }

    let cancelled = false;
    // null: még nem tudjuk a kiindulási értéket, tehát nincs mihez képest növekedés
    let lastSeen: number | null = null;

    const check = async () => {
      try {
        const count = await registrationsAPI.getPendingCount();
        if (cancelled) return;

        setPendingRegistrations(count);

        const isFirstCheck = lastSeen === null;
        if (count > 0 && (isFirstCheck || count > lastSeen!)) {
          showToast(
            count === 1
              ? 'Új regisztráció vár jóváhagyásra.'
              : `${count} regisztráció vár jóváhagyásra.`,
            'info'
          );
        }

        lastSeen = count;
      } catch {
        // Hálózati hiba: a következő körben újra próbáljuk
      }
    };

    check();
    const timer = setInterval(check, 60_000);

    // Elbírálás után ne kelljen a következő körre várni a menü jelzésével
    const onChanged = () => check();
    window.addEventListener(REGISTRATIONS_CHANGED_EVENT, onChanged);

    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener(REGISTRATIONS_CHANGED_EVENT, onChanged);
    };
  }, [user, showToast]);

  // Global Cmd+K / Ctrl+K shortcut for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const topItem = topNavItem;
  const aiStatus = useAiStatus();
  // Amíg a klub beállításai nem töltődtek be, a Tagdíjak elrejtve marad -
  // ez kisebb villanás, mint ha előbb megjelenne, aztán eltűnne.
  const navigationGroups = getNavigationGroups(user, clubSettings?.membershipFeesEnabled ?? false, aiStatus.available);

  const isCurrentPage = (href: string) => {
    return location.pathname === href;
  };

  const renderNavItem = (item: { name: string; href: string; icon: any }, onClick?: () => void) => (
    <Link
      key={item.name}
      to={item.href}
      onClick={onClick}
      className={`mb-1 flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
        isCurrentPage(item.href)
          ? 'bg-primary-100 text-primary-700'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`}
    >
      <div className="flex items-center">
        <item.icon className="mr-3 h-5 w-5" />
        {item.name}
      </div>
      {item.href === '/messages' && unreadCount > 0 && (
        <span className="bg-primary-600 text-white text-xs font-semibold rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center">
          {unreadCount}
        </span>
      )}
      {item.href === '/board' && boardUnread > 0 && (
        <span className="bg-amber-500 text-white text-xs font-semibold rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center">
          {boardUnread}
        </span>
      )}
      {item.href === '/registrations' && pendingRegistrations > 0 && (
        <span className="bg-amber-500 text-white text-xs font-semibold rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center">
          {pendingRegistrations}
        </span>
      )}
    </Link>
  );

  const renderNavigation = (onItemClick?: () => void) => (
    <>
      {renderNavItem(topItem, onItemClick)}
      {navigationGroups.map((group) => {
        const hasActive = group.items.some(item => isCurrentPage(item.href));
        // Egyetlen csoportot (pl. a vendég "Naptár" menüje) nincs értelme becsukni
        const isExpanded = navigationGroups.length === 1 || (expandedGroups[group.title] ?? false) || hasActive;
        return (
          <div key={group.title} className="mt-2">
            <button
              type="button"
              aria-expanded={isExpanded}
              onClick={() => toggleGroup(group.title)}
              className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-700 transition-colors"
            >
              <span>{group.title}</span>
              <ChevronDownIcon
                className={`h-3.5 w-3.5 transition-transform ${isExpanded ? '' : '-rotate-90'}`}
              />
            </button>
            {isExpanded && (
              <div className="mt-1">
                {group.items.map((item) => renderNavItem(item, onItemClick))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-grow flex-col overflow-y-auto bg-white shadow-sm">
          {/* Ha a klub megadta a nevét, az övé a fejléc - a címerével együtt. Név
              nélkül a Portero lockup áll be, mert a klubnév helyén megjelenő
              csupasz terméknév a jel mellett kettős emblémának látszana. */}
          <div className="flex h-16 items-center px-4 gap-2">
            {clubSettings?.clubName ? (
              <>
                <img
                  src={clubLogoSrc(clubSettings.crestUrl)}
                  alt=""
                  className="h-9 w-9 object-contain"
                  onError={handleClubLogoError}
                />
                <h1 className="text-xl font-semibold text-gray-900">{clubSettings.clubName}</h1>
              </>
            ) : (
              <h1>
                <BrandLockup size="md" />
              </h1>
            )}
          </div>
          <nav className="mt-8 flex-1 px-4 pb-4">
            {renderNavigation()}
          </nav>
          {/* Funkciók és újdonságok: amíg van meg nem nézett újdonság, pötty jelzi */}
          <div className="border-t border-gray-100 px-4 py-3">
            <Link
              to="/features"
              className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                isCurrentPage('/features')
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center">
                <SparklesIcon className="mr-3 h-5 w-5" />
                Funkciók és újdonságok
              </span>
              {hasNews && <span className="h-2.5 w-2.5 rounded-full bg-amber-500" aria-label="Van új funkció" />}
            </Link>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        {/* Felső sáv: mobilon rejtve, ott az alsó navigáció + menülap látja el a szerepét */}
        <div className="sticky top-0 z-40 hidden h-16 shrink-0 items-center bg-white shadow-sm lg:flex">
          <div className="flex flex-1 justify-end px-4">
            <div className="flex items-center gap-1">
              {/* Search button */}
              <button
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                title="Keresés (⌘K)"
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
                <span className="hidden sm:inline text-sm text-gray-400">Keresés...</span>
                <kbd className="hidden sm:inline-flex items-center rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] text-gray-400">
                  ⌘K
                </kbd>
              </button>

              {/* Tanuló mód: útmutató az oldal tetején és ? jelek a gombok mellett */}
              <button
                type="button"
                onClick={toggleLearningMode}
                aria-pressed={learningMode}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                  learningMode
                    ? 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                }`}
                title={learningMode ? 'Tanuló mód kikapcsolása' : 'Tanuló mód bekapcsolása'}
              >
                <AcademicCapIcon className="h-5 w-5" />
                <span className="hidden xl:inline">Tanuló mód</span>
              </button>

              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center space-x-3 rounded-lg px-3 py-2 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-center h-9 w-9 rounded-full bg-primary-100 text-primary-600">
                    <UserCircleIcon className="h-6 w-6" />
                  </div>
                  <div className="text-sm text-left hidden sm:block">
                    <p className="font-medium text-gray-700">{user?.name}</p>
                    <p className="text-gray-500 text-xs">{describeRoles(user)}</p>
                  </div>
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                </Menu.Button>

                <Transition
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none divide-y divide-gray-100">
                    <div className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>

                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/profile"
                            className={`${
                              active ? 'bg-gray-50' : ''
                            } flex items-center px-4 py-2 text-sm text-gray-700`}
                          >
                            <Cog6ToothIcon className="mr-3 h-5 w-5 text-gray-400" />
                            Profil beállítások
                          </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/help"
                            className={`${
                              active ? 'bg-gray-50' : ''
                            } flex items-center px-4 py-2 text-sm text-gray-700`}
                          >
                            <QuestionMarkCircleIcon className="mr-3 h-5 w-5 text-gray-400" />
                            Súgó
                          </Link>
                        )}
                      </Menu.Item>
                    </div>

                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleLogout}
                            className={`${
                              active ? 'bg-gray-50' : ''
                            } flex w-full items-center px-4 py-2 text-sm text-red-600`}
                          >
                            <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-red-400" />
                            Kijelentkezés
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6 pb-24 lg:pb-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <ModuleGuide />
            {children}
          </div>
        </main>
      </div>

      {/* Mobil alsó navigáció + felúszó menülap */}
      <BottomNav
        onOpenSearch={() => setSearchOpen(true)}
        onOpenMenu={() => setMobileMenuOpen(true)}
        menuOpen={mobileMenuOpen}
      />
      <MobileMenuSheet open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Search modal */}
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Global modals */}
      <EventForm
        isOpen={modals.eventForm.isOpen}
        onClose={closeEventForm}
        event={modals.eventForm.event}
        onSave={() => { closeEventForm(); onEventSaved?.(); }}
        selectedDate={modals.eventForm.selectedDate}
      />
      <PlayerModal
        isOpen={modals.playerForm.isOpen}
        onClose={closePlayerForm}
        player={modals.playerForm.player}
        onSave={() => { closePlayerForm(); onPlayerSaved?.(); }}
      />
    </div>
  );
};

export default Layout;